import { useState, useEffect } from 'react';
import { backendUrl, tenantId } from 'config/envConfig';
import { applyPasswordPolicy, applyGoogleCaptcha } from 'config';
import { i18n } from 'utils/i18n';
import { reportConfigLoadError } from './configObservability';

/**
 * Merge per-tenant locale overrides (from visual-config) into i18n PRE-login.
 * `getLocale` is post-login, so without this the pre-login screens (reset/
 * create-password, login) show the generic text instead of the tenant's.
 * Generic for any tenant/lang, not just one string.
 */
const applyLocaleOverrides = (localeOverrides) => {
  if (!localeOverrides) return;
  Object.keys(localeOverrides).forEach((lang) => {
    if (localeOverrides[lang]) {
      i18n.addResources(lang, 'translations', localeOverrides[lang]);
    }
  });
};

const CONFIG_LOAD_TIMEOUT_MS = 10000;
const getCacheKey = () => `dashboard_visual_config_${tenantId}`;
const getAssetsCacheName = () => `tenant-assets-${tenantId}`;

const fetchWithTimeout = (url, options, timeoutMs) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timer),
  );
};

const cacheAssets = async (visualConfig, assetsCacheName) => {
  try {
    if (!('caches' in window)) return;
    const cache = await caches.open(assetsCacheName);
    const urls = [visualConfig.themeUrl, visualConfig.logoUrl].filter(Boolean);
    for (const url of urls) {
      const cached = await cache.match(url);
      if (!cached) {
        await cache.add(url);
      }
    }
  } catch (err) {
    console.warn('Asset caching failed', err);
  }
};

/**
 * Pre-login hook: fetches visual config (branding) from BFF.
 * Falls back to localStorage cache if the request fails or times out.
 * Invalidates cached assets when configVersion changes.
 */
const useVisualConfig = () => {
  const cacheKey = getCacheKey();
  const assetsCacheName = getAssetsCacheName();

  const [visualConfig, setVisualConfig] = useState(() => {
    let cachedConfig = null;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        cachedConfig = JSON.parse(cached);
      } else {
        // Cache miss — the resolved tenantId may not match the cached one
        // (e.g. env var fallback points to a different tenant, or backend is
        // down so we couldn't resolve). Search for any cached visual config.
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('dashboard_visual_config_')) {
            const found = localStorage.getItem(key);
            if (found) { cachedConfig = JSON.parse(found); break; }
          }
        }
      }
    } catch {
      cachedConfig = null;
    }
    // Apply the tenant password policy from cache on first paint, so the
    // pre-login reset/create-password screens show the right rules before the
    // network refresh (these flows never reach bootstrap).
    if (cachedConfig) {
      applyPasswordPolicy(cachedConfig.passwordPolicy, cachedConfig.passwordSubmitPolicy);
      applyGoogleCaptcha(cachedConfig.googleCaptcha);
      applyLocaleOverrides(cachedConfig.localeOverrides);
    }
    return cachedConfig;
  });
  const [loading, setLoading] = useState(true);
  const [backendReachable, setBackendReachable] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetchWithTimeout(
          `${backendUrl}/api/v1/tenant/visual-config`,
          {},
          CONFIG_LOAD_TIMEOUT_MS,
        );

        if (!response.ok) {
          throw new Error(`visual-config responded ${response.status}`);
        }

        const data = await response.json();

        if (cancelled) return;

        // Invalidate asset cache if configVersion changed
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const prev = JSON.parse(cached);
            if (prev.configVersion !== data.configVersion && 'caches' in window) {
              await caches.delete(assetsCacheName);
            }
          } catch {
            // ignore parse errors on stale cache
          }
        }

        // Resolve relative asset URLs against the BFF origin
        const resolved = { ...data };
        ['logoUrl', 'logoLoginUrl', 'faviconUrl', 'themeUrl'].forEach((key) => {
          if (resolved[key] && resolved[key].startsWith('/')) {
            resolved[key] = `${backendUrl}${resolved[key]}`;
          }
        });

        // Wire the tenant password policy + locale overrides BEFORE
        // setVisualConfig so the re-render reads the resolved rules/text
        // (mutating config/i18n alone doesn't re-render). Covers reset/create-
        // password and login (pre-login, no bootstrap).
        applyPasswordPolicy(resolved.passwordPolicy, resolved.passwordSubmitPolicy);
        applyGoogleCaptcha(resolved.googleCaptcha);
        applyLocaleOverrides(resolved.localeOverrides);

        localStorage.setItem(cacheKey, JSON.stringify(resolved));
        setVisualConfig(resolved);
        if (!cancelled) setBackendReachable(true);

        // Cache theme CSS and logo in localStorage for offline fallback
        const tid = resolved.tenantId;
        if (tid) {
          if (resolved.themeUrl) {
            fetch(resolved.themeUrl)
              .then((r) => r.ok ? r.text() : null)
              .then((css) => { if (css) localStorage.setItem(`dashboard_theme_css_${tid}`, css); })
              .catch(() => {});
          }
          const logo = resolved.logoLoginUrl || resolved.logoUrl;
          if (logo) {
            fetch(logo)
              .then((r) => r.ok ? r.text() : null)
              .then((svg) => { if (svg) localStorage.setItem(`dashboard_logo_${tid}`, svg); })
              .catch(() => {});
          }
          if (resolved.faviconUrl) {
            fetch(resolved.faviconUrl)
              .then((r) => {
                if (!r.ok) return null;
                return r.blob().then((b) => new Promise((res) => {
                  const reader = new FileReader();
                  reader.onloadend = () => res(reader.result);
                  reader.readAsDataURL(b);
                }));
              })
              .then((dataUrl) => { if (dataUrl) localStorage.setItem(`dashboard_favicon_${tid}`, dataUrl); })
              .catch(() => {});
          }
        }

        // Pre-cache assets in background
        cacheAssets(resolved, assetsCacheName);
      } catch (err) {
        console.warn('Visual config fetch failed, using cached', err);
        reportConfigLoadError('visual-config', err);
        if (!cancelled) setBackendReachable(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  return { visualConfig, loading, backendReachable };
};

export default useVisualConfig;
